<template>
  <b-container class="pb-3">

    <div class="reg-steps mb-2">
      <div class="reg-step" :class="{ 'reg-step--active': currentStep === 0, 'reg-step--done': currentStep > 0 }">
        <div class="reg-step__circle">
          <feather-icon v-if="currentStep > 0" icon="CheckIcon" size="14" />
          <span v-else>1</span>
        </div>
        <span class="reg-step__label">Disclaimer</span>
        <div class="reg-step__line" />
      </div>

      <div class="reg-step" :class="{ 'reg-step--active': currentStep === 1, 'reg-step--done': currentStep > 1 }">
        <div class="reg-step__circle">
          <feather-icon v-if="currentStep > 1" icon="CheckIcon" size="14" />
          <span v-else>2</span>
        </div>
        <span class="reg-step__label">Your Details</span>
        <div class="reg-step__line" />
      </div>

      <div class="reg-step" :class="{ 'reg-step--active': currentStep === 2, 'reg-step--done': currentStep > 2 }">
        <div class="reg-step__circle">
          <span>3</span>
        </div>
        <span class="reg-step__label">Application Received</span>
      </div>
    </div>

    <!-- Message for if logged in (Hi x You are already logged in [go back])-->
    <RegistrationDisclaimer v-if="showDisclaimer" @agreed="showDisclaimer = false"/>

    <!-- Application received -->
    <b-card v-else-if="registrationComplete" class="reg-complete-card">
      <div class="reg-complete-icon-wrap mb-2">
        <feather-icon icon="CheckCircleIcon" size="48" class="reg-complete-icon" />
      </div>
      <h3 class="reg-complete-title">Application Received</h3>
      <p class="reg-complete-text">
        Thank you for registering for the Structured Edge platform. We have sent a verification email to
        <strong>{{ registeredEmail }}</strong>. Please follow the link inside to confirm your email address and complete your application.
      </p>
      <div class="reg-complete-note">
        <feather-icon icon="InfoIcon" size="14" class="mr-50" />
        Didn't receive the email? Check your spam folder or resend it below.
      </div>
      <div class="mt-2">
        <b-button
          type="button"
          variant="outline-primary"
          :disabled="resendingVerification || resendCooldown > 0"
          @click="resendVerificationEmail"
        >
          {{ resendingVerification ? 'Sending…' : resendCooldown > 0 ? 'Resend available in ' + resendCooldown + 's' : 'Resend verification email' }}
        </b-button>
        <p v-if="resendMessage" class="mt-1 mb-0" :class="resendError ? 'text-danger' : 'text-success'" role="status" aria-live="polite">
          {{ resendMessage }}
        </p>
      </div>
    </b-card>

    <!-- Registration form -->
    <b-card v-else class="reg-form-card">
      <b-form @submit.prevent="handleRegistration">
        <div class="reg-form-header mb-2">
          <h3 class="reg-form-title">Your Details</h3>
          <p class="reg-form-subtitle">Please fill in all required fields below to register for the platform.</p>
        </div>

        <div class="reg-form-section mb-1">
          <p class="reg-form-section-label">Account</p>
          <b-form-group label="Username">
            <b-form-input v-model="form.username" placeholder="" :state="submitted ? !errors.username ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.username }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="reg-form-section mb-1">
          <p class="reg-form-section-label">Personal Information</p>
          <b-row>
            <b-col md="6">
              <b-form-group label="First Name">
                <b-form-input v-model="form.firstName" placeholder="" :state="submitted ? !errors.firstName ? null : false : null"/>
                <b-form-invalid-feedback>{{ errors.firstName }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Surname">
                <b-form-input v-model="form.surname" placeholder="" :state="submitted ? !errors.surname ? null : false : null"/>
                <b-form-invalid-feedback>{{ errors.surname }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Email Address">
            <b-form-input v-model="form.email" placeholder="" :state="submitted ? !errors.email ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.email }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Confirm Email Address">
            <b-form-input v-model="form.confirmationEmail" placeholder="" :state="submitted ? !errors.confirmationEmail ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.confirmationEmail }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Telephone Number">
            <b-form-input v-model="form.telephone" placeholder="" :state="submitted ? !errors.telephone ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.telephone }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="reg-form-section mb-1">
          <p class="reg-form-section-label">Company Details</p>
          <b-form-group label="Company Name">
            <b-form-input v-model="form.companyName" placeholder="" :state="submitted ? !errors.companyName ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.companyName }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Company Address">
            <b-form-input v-model="form.companyAddress" placeholder="" :state="submitted ? !errors.companyAddress ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.companyAddress }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Company Post Code">
            <b-form-input v-model="form.companyPostcode" placeholder="" :state="submitted ? !errors.companyPostcode ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.companyPostcode }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Country Business is Based In">
            <b-form-select :options="countries" v-model="form.country" :state="submitted ? !errors.country ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.country }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Firm FCA Number">
            <b-form-input v-model="form.firmFCANumber" placeholder="" :state="submitted ? !errors.firmFCANumber ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.firmFCANumber }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Company Type">
            <b-form-select :options="companyTypes" v-model="form.companyType" :state="submitted ? !errors.companyType ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.companyType }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Further Information (if applicable)">
            <b-form-input v-model="form.furtherInfo" placeholder="Any additional details" :state="submitted ? !errors.furtherInfo ? null : false : null"/>
            <b-form-invalid-feedback>{{ errors.furtherInfo }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div class="reg-form-section mb-2">
          <p class="reg-form-section-label">Preferences</p>
          <b-form-group class="mb-0">
            <b-form-checkbox v-model="form.emailOptIn" switch>
              Receive optional site updates via email
            </b-form-checkbox>
          </b-form-group>
        </div>
You will initially receive report publication alert emails. This can be changed on your account details page <br><br>
        <div class="d-flex justify-content-end">
          <b-button type="submit" variant="primary">
            Submit Application
          </b-button>
        </div>
      </b-form>
    </b-card>
  </b-container>
</template>

<script>
import {
  BCard, BButton, BForm, BFormGroup, BFormInput, BFormSelect, BFormInvalidFeedback, BFormCheckbox, BRow, BCol, BContainer,
} from 'bootstrap-vue'
import RegistrationDisclaimer from '@/views/Registration/RegistrationDisclaimer.vue'

const getInitialForm = () => ({
  username: '',
  firstName: '',
  surname: '',
  email: '',
  confirmationEmail: '',
  companyName: '',
  companyAddress: '',
  companyPostcode: '',
  country: null,
  telephone: '',
  firmFCANumber: '',
  companyType: null,
  furtherInfo: '',
  emailOptIn: false,
})

export default {
  components: {
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormInvalidFeedback,
    BFormCheckbox,
    BRow,
    BCol,
    BContainer,
    RegistrationDisclaimer,
  },
  computed: {
    currentStep() {
      if (this.showDisclaimer) return 0
      if (this.registrationComplete) return 2
      return 1
    },
    requiresFcaNumber() {
      return [0, 2].includes(Number(this.form.companyType))
        && this.form.companyType !== null
    },
  },
  data() {
    return {
      registrationComplete: false,
      registeredEmail: '',
      resendingVerification: false,
      resendCooldown: 0,
      resendTimer: null,
      resendRequestId: 0,
      resendMessage: '',
      resendError: false,
      submitted: false,
      showDisclaimer: true,
      errors: {
      },
      form: getInitialForm(),
      /* form: {
        username: 'judewallace1',
        firstName: 'Jude',
        surname: 'Wallace',
        email: 'judewallace@standard.com',
        confirmationEmail: 'judewallace@standard.com',
        companyName: 'FVC',
        companyAddress: 'idk',
        country: 1,
        telephone: '012121',
        firmFCANumber: '187724',
        companyType: 0,
        furtherInfo: 'No thanks',
        accountType: null,
      }, */
      countries: [],
      companyTypes: [],
    }
  },
  created() {
    this.getDropdowns()
  },
  activated() {
    this.resendRequestId += 1
    this.resendingVerification = false
    this.showDisclaimer = true
    this.registrationComplete = false
    this.registeredEmail = ''
    this.clearResendTimer()
    this.resendCooldown = 0
    this.resendMessage = ''
    this.resendError = false
  },
  beforeDestroy() {
    this.resendRequestId += 1
    this.clearResendTimer()
  },
  deactivated() {
    this.resendRequestId += 1
    this.clearResendTimer()
  },
  methods: {
    clearResendTimer() {
      if (this.resendTimer) clearInterval(this.resendTimer)
      this.resendTimer = null
    },
    startResendCooldown(seconds) {
      this.clearResendTimer()
      this.resendCooldown = Math.max(0, Math.ceil(Number(seconds) || 0))
      if (!this.resendCooldown) return
      this.resendTimer = setInterval(() => {
        this.resendCooldown = Math.max(0, this.resendCooldown - 1)
        if (!this.resendCooldown) this.clearResendTimer()
      }, 1000)
    },
    async resendVerificationEmail() {
      if (!this.registrationComplete || !this.registeredEmail || this.resendingVerification || this.resendCooldown > 0) return
      const requestId = this.resendRequestId
      this.resendingVerification = true
      this.resendMessage = ''
      this.resendError = false
      try {
        const { data } = await this.$http.post('userregistration.cfc?method=resendRegistrationVerificationEmail', new URLSearchParams({
          emailaddress: this.registeredEmail,
        }))
        if (requestId !== this.resendRequestId) return
        this.resendError = !data.status
        this.resendMessage = data.message || (data.status ? 'Verification email resent. Please check your inbox.' : 'We could not resend the verification email. Please try again.')
      } catch (error) {
        if (requestId !== this.resendRequestId) return
        this.resendError = true
        this.resendMessage = 'We could not resend the verification email. Please try again.'
      } finally {
        if (requestId === this.resendRequestId) {
          this.resendingVerification = false
          this.startResendCooldown(15)
        }
      }
    },
    resetForm() {
      this.form = getInitialForm()
      this.errors = {}
      this.submitted = false
    },
    getDropdowns() {
      this.$http.get('auth.cfc?method=getCountryList')
        .then(response => {
          this.countries = response.data.list
        })

      this.$http.get('auth.cfc?method=getCompanyTypes')
        .then(response => {
          this.companyTypes = response.data.list
        })
    },
    validateFormEntry() {
      this.errors = {}

      const dropdownError = 'Please select an option from the dropdown'
      const generalBlankError = 'Please fill in this field'

      if (!this.form.username.trim()) {
        this.errors.username = generalBlankError
      }

      if (!this.form.firstName.trim()) {
        this.errors.firstName = generalBlankError
      }

      if (!this.form.surname.trim()) {
        this.errors.surname = generalBlankError
      }

      if (!this.form.email.trim()) {
        this.errors.email = generalBlankError
      }

      if (!this.form.confirmationEmail.trim()) {
        this.errors.confirmationEmail = generalBlankError
      }

      if (!this.form.companyName.trim()) {
        this.errors.companyName = generalBlankError
      }

      if (!this.form.companyAddress.trim()) {
        this.errors.companyAddress = generalBlankError
      }

      if (!this.form.companyPostcode.trim()) {
        this.errors.companyPostcode = generalBlankError
      }

      if (this.form.country === null) {
        this.errors.country = dropdownError
      }

      if (!this.form.telephone.trim()) {
        this.errors.telephone = generalBlankError
      }

      if (this.form.companyType === null) {
        this.errors.companyType = dropdownError
      }

      if (!this.form.furtherInfo.trim() && this.form.companyType === 5) {
        this.errors.furtherInfo = generalBlankError
      }
      // Overload checks
      if (this.form.email.trim() && !this.form.email.trim().includes('@')) {
        this.errors.email = 'Please enter a valid email address'
      }
      if (this.form.email.trim() && this.form.confirmationEmail.trim() && this.form.email.trim() !== this.form.confirmationEmail.trim()) {
        this.errors.confirmationEmail = 'Confirmation email address does not match'
      }

      if (this.form.telephone.trim() && !/^[\d\s()+-]+$/.test(this.form.telephone.trim())) {
        this.errors.telephone = 'Please enter a valid telephone number'
      }

      if (this.requiresFcaNumber && !this.form.firmFCANumber.trim()) {
        this.errors.firmFCANumber = 'Please enter your firm FCA number'
      }

      if (this.form.firmFCANumber.trim() && !/^[\d]+$/.test(this.form.firmFCANumber.trim())) {
        this.errors.firmFCANumber = 'Please enter a valid FCA number'
      }
    },

    handleRegistration() {
      this.validateFormEntry()
      this.submitted = true
      if (Object.keys(this.errors).length === 0) {
        this.$http.post('userregistration.cfc?method=registerNewUser', new URLSearchParams({
          username: this.form.username,
          firstname: this.form.firstName,
          surname: this.form.surname,
          emailaddress: this.form.email,
          companyname: this.form.companyName,
          companyaddress: this.form.companyAddress,
          companypostcode: this.form.companyPostcode,
          country: Number(this.form.country),
          telephone: this.form.telephone,
          companyfcanumber: this.form.firmFCANumber,
          companytype: Number(this.form.companyType),
          emailpreferences: this.form.emailOptIn,
          furtherinformation: this.form.furtherInfo,
        }))
          .then(response => {
            if (!response.data.status) {
              if ('errors' in response.data) {
                this.errors = response.data.errors
              }
            } else {
              this.registeredEmail = this.form.email.trim()
              this.resetForm()
              this.registrationComplete = true
              this.resendMessage = ''
              this.resendError = false
              this.startResendCooldown(15)
            }
          })
      }
    },
  },
}
</script>

<style scoped>
.reg-steps {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1.5rem 0 0.5rem;
}

.reg-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  max-width: 160px;
}

.reg-step__circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #d0d5dd;
  background: #fff;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
  position: relative;
  z-index: 1;
}

.reg-step__label {
  margin-top: 8px;
  font-size: 0.75rem;
  text-align: center;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.2s;
}

.reg-step__line {
  position: absolute;
  top: 18px;
  left: calc(50% + 18px);
  right: calc(-50% + 18px);
  height: 2px;
  background: #d0d5dd;
  transition: background 0.2s;
}

/* Active step */
.reg-step--active .reg-step__circle {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}
.reg-step--active .reg-step__label {
  color: var(--primary);
}

/* ── Registration form ── */
.reg-form-card {
  max-width: 900px;
  margin: 0 auto;
}

.reg-form-header {
  border-bottom: 1px solid #ebe9f1;
  padding-bottom: 1rem;
}

.reg-form-title {
  font-size: 1.375rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.reg-form-subtitle {
  font-size: 0.9rem;
  margin-bottom: 0;
}

.reg-form-section {
  border-left: 3px solid var(--primary);
  padding-left: 1rem;
}

.reg-form-section-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--primary);
  margin-bottom: 0.75rem;
}

/* ── Application received ── */
.reg-complete-card {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}

.reg-complete-icon-wrap {
  display: flex;
  justify-content: center;
}

.reg-complete-icon {
  color: #28c76f;
}

.reg-complete-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.reg-complete-text {
  font-size: 0.9rem;
  line-height: 1.7;
  margin-bottom: 0.75rem;
}

.reg-complete-note {
  display: inline-flex;
  align-items: center;
  background: #f0f4ff;
  border: 1px solid #d5d9f0;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

/* Completed step */
.reg-step--done .reg-step__circle {
  border-color: #28c76f;
  background: #28c76f;
  color: #fff;
}
.reg-step--done .reg-step__label {
  color: #28c76f;
}
.reg-step--done .reg-step__line {
  background: #28c76f;
}
</style>
